import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers, {
  StatisticsProps,
} from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import {
  COURSE_FIRST_FOLD,
  COURSE_NAMES,
  FAQ,
} from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { formatSeoConfig } from '@lib/seo'
import { defineMessage, Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useState } from 'react'
import ExpandableActivities from '@components/course-landing-page/expandable-activities'
import CourseQuizzes from '@components/course-landing-page/quizzes'
import ExploreTools, {
  ToolCardData,
} from '@components/course-landing-page/explore-tools'
import CodingalSubscription from '@components/home/codingal-subscription'
import CodingalProductBlog from '@components/blog/product-blogs'
import AboutUsCard from '@components/reviews-and-testimonials/about-us-card'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import HowClassWorks from '@components/course-landing-page/how-class-works'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { CODING_GRADE_11_12 } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['cgm'].trialCourseQuery}`

const STATISTICS: StatisticsProps[] = [
  {
    title: '🎓 1,000+',
    description: defineMessage({ message: 'Expert CS instructors' }),
    backgroundColor: 'bg-orange-200',
  },
  {
    title: '👨‍💻 7 years',
    description: defineMessage({ message: 'Average teaching experience' }),
    backgroundColor: 'bg-blue-50',
  },
  {
    title: '👩 91%',
    description: defineMessage({ message: 'Female faculty' }),
    backgroundColor: 'bg-yellow-100',
  },
  {
    title: '💻 500+',
    description: defineMessage({ message: 'Lessons and projects' }),
    backgroundColor: 'bg-green-50',
  },
]

const GRADE_11_12_TOOLS: ToolCardData[] = [
  {
    title: 'Python',
    description:
      'Write advanced Python code to build tools, automate tasks and solve real problems.',
    image: '/images/courses/coding-for-kids/python.jpg',
  },
  {
    title: 'Website Development',
    description:
      'Build and design real websites using HTML, CSS and advanced JavaScript.',
    image: '/images/courses/coding-for-kids/website.jpg',
  },
  {
    title: 'Java',
    description:
      'Write and run Java code to build structured, object-oriented programs and solve complex problems.',
    image: '/images/courses/coding-for-kids/java.jpg',
  },
  {
    title: 'Data Science & ML',
    description:
      'Analyse real datasets, build predictive models and apply data science techniques.',
    image: '/images/courses/coding-for-kids/data-science-and-ml.jpg',
  },
  {
    title: 'AI Code 101',
    description:
      'Explore how AI works, build models and apply machine learning to real-world problems.',
    image: '/images/courses/coding-for-kids/ai-code-101.jpg',
  },
  {
    title: 'Git and GitHub',
    description:
      'Learn version control, manage code changes and collaborate on projects like a real developer.',
    image: '/images/courses/coding-for-kids/git.jpg',
  },
]

const CodingGrade1112 = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }
  useStoreUTMParams()

  const seoConfig = formatSeoConfig({
    title: 'Online Coding Classes Grades 11-12 (Ages 16-18)',
    description:
      'Prepare for college and tech careers with LIVE 1-on-1 coding & AI classes for Grades 11 & 12. Advanced Python, AI & more for ages 16-18. Try a free lesson!',
    url: 'https://www.codingal.com/coding-for-kids/grade-11-12/',
    metaImage:
      'https://www.codingal.com/images/courses/coding-for-kids/grade-11-12/og.jpg',
  })

  return (
    <>
      <NextSeo {...seoConfig} />
      <FAQPageJsonLd
        mainEntity={FAQ[CODING_GRADE_11_12].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName={COURSE_FIRST_FOLD[CODING_GRADE_11_12].name}
        description={COURSE_FIRST_FOLD[CODING_GRADE_11_12].description}
        provider={{ '@type': 'Organization', name: 'Codingal' }}
        offers={{ '@type': 'Offer', category: 'Paid' }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT20H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={CODING_GRADE_11_12}
        urlQuery={urlQuery}
        isPlatformPage
        hideReadMore
        background="bg-white"
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={CODING_GRADE_11_12} />
          <RecommendedCourses
            courseCodes={['cgm', 'cp', 'cc', 'wd', 'pc']}
            title="Best coding classes for teens aged 16 to 18"
            description={
              <>
                Master full-stack development, advanced Python, data science,
                AI, and AP Computer Science with expert live instruction.
              </>
            }
            showAllCoursesButton
          />
          <ExploreTools
            title="What do 16 to 18 year olds explore in Codingal coding classes?"
            description="Junior and senior students work at an advanced level across AI, data science, and competitive programming, tackling projects that go well beyond the classroom. As upper high schoolers refine their skills, college-bound coders explore specialised pathways that prepare them for university and tech careers."
            urlQuery={urlQuery}
            tools={GRADE_11_12_TOOLS}
            note="⭐ As junior and senior students advance, they may also explore specialized AI pathways, data science projects, and competitive programming."
          />
          <CourseFeatures
            courseTitle={CODING_GRADE_11_12}
            title={
              <Trans>
                An engaging coding journey for teens in Grades 11 and 12
              </Trans>
            }
          />
          <Activities courseName={CODING_GRADE_11_12} urlQuery={urlQuery} />
          <Teachers
            urlQuery={urlQuery}
            title={<Trans>Learn from expert Computer Science teachers</Trans>}
            statistics={STATISTICS}
            description={
              <Trans>
                Codingal&apos;s Grade 11 and 12 teachers are trained Computer
                Science educators with 7 or more years of classroom experience.
                Every instructor completes a structured onboarding program
                before teaching, and is regularly evaluated by student and
                parent ratings.
                <br />
                Teachers are rated 4.9 out of 5 by parents and students. The
                Codingal curriculum is rated 4.8 out of 5. The company Net
                Promoter Score (NPS) is 86 as of Q1 2026.
              </Trans>
            }
          />
          <HowClassWorks courseName={CODING_GRADE_11_12} urlQuery={urlQuery} />
          <CodingForStudents
            title="Our coding courses for Grades 11 and 12 are designed for students with ambition and a clear direction"
            description="Our coding courses for Grades 11 and 12 are designed for students with ambition and a clear direction. Junior and senior students work at an advanced level across AI, data science, competitive programming, and full stack development through expert-led live Codingal classes built around real outcomes. Upper high schoolers build impressive project portfolios and earn recognized certifications alongside their coursework. As college-bound coders in their final years of school, students develop the advanced technical ability, independent project leadership, and competitive programming skills needed to stand out in college applications and hit the ground running in tech careers."
            imagePath="/images/courses/coding-for-kids/grade-11-12/coding"
            imageWidth={213}
            imageHeight={219}
            reverse
          />
          <ExpandableActivities
            title="100+ coding projects your child will love building"
            description="A guided coding journey where students learn by creating, exploring, and building one step at a time."
            courseTitle={CODING_GRADE_11_12}
            urlQuery={urlQuery}
          />
          <CodingalSubscription compact subject="ELA" urlQuery={urlQuery} />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
            subject="coding"
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description="At Codingal, junior and senior students advance through a STEM.org accredited program built on proven educational frameworks like Bloom's Taxonomy, BIDE, and STEAM. Our curriculum is aligned with Common Core, state standards, AP Computer Science goals, and the K12 Computer Science Framework, ensuring learning that is academically rigorous and directly relevant to college applications and tech careers. Through hands-on, project-based learning, college-bound coders build machine learning models, data science projects, and full stack applications that apply advanced math, science, and engineering logic to real-world problems. Backed by STEM.org's rigorous audits, every lesson builds a future-proof foundation that prepares students for university and beyond."
          />
          <CodingCertificate
            title="Our coding classes are built for students with goals beyond the classroom"
            description="At Codingal, our Grades 11 and 12 curriculum takes students from strong programming foundations to advanced, industry-relevant development across AI, data science, and full stack technologies. We begin with structured challenges that push analytical thinking, independent problem-solving, and technical depth through ambitious project work. As upper high schoolers refine their skills, they specialize in pathways that align with their college and career goals, from competitive programming to machine learning and beyond. With every project they complete, college-bound coders build the professional-level ability, recognized certifications, and impressive portfolio needed to stand out in a competitive world."
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our coding classes are built for students thinking ahead"
            description="At Codingal, our Grades 11 and 12 curriculum takes students from strong programming foundations to advanced, industry-relevant work across AI, data science, and full stack development. We begin with structured challenges that push analytical thinking, independent problem-solving, and technical depth through ambitious, real-world project work. As upper high schoolers refine their skills, they specialize in pathways that align with their college and career goals, from competitive programming to machine learning and beyond. With every project they complete, college-bound coders build the professional-level ability and impressive portfolio needed to stand out where it matters most."
            imagePath="/images/courses/ai-and-ml/ai-course-students"
            imageWidth={235}
            imageHeight={241}
          />
          <Investors isCoursePage urlQuery={urlQuery} />
          <Faq
            courseName={CODING_GRADE_11_12}
            title="FAQs for Coding Classes for Grades 11 and 12"
          />
          <CourseQuizzes courseName={CODING_GRADE_11_12} />
          <CodingalProductBlog
            urlQuery={urlQuery}
            isCoursePage
            category="coding-for-kids"
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={CODING_GRADE_11_12}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <AboutUsCard />
      <FooterBanner
        title="AI & coding classes for Grade 11-12 teens with the best AI & coding teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return { props: {} }
}

export default CodingGrade1112
